import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
} from '@mui/material';
import { ref, set } from 'firebase/database';
import { database, DB_PATHS } from '../config/firebase';
import dayjs from 'dayjs';

interface NewManager {
  phonenumber: string;
  username: string;
  password: string;
  grade: 'localmanager';
  appname: string;
  block: string;
  referralcode: string;
  // 충청 앱 필드들
  registrant?: string; // 등록자 전번 필드
  registeredBy?: string; // 수수료전번 필드
  extensionAmount?: number; // 연장금액 필드
  fee?: number; // 수수료 필드
}

interface ChungcheongManagerRegistrationProps {
  open: boolean;
  onClose: () => void;
  currentUser: any; // 현재 로그인한 관리자 정보
  onManagerAdded?: () => void; // 관리자 추가 후 목록 갱신을 위한 콜백
}

const ChungcheongManagerRegistration: React.FC<ChungcheongManagerRegistrationProps> = ({
  open,
  onClose,
  currentUser,
  onManagerAdded,
}) => {
  const [newManager, setNewManager] = useState<NewManager>({
    phonenumber: '',
    username: '',
    password: '',
    grade: 'localmanager',
    appname: 'smlink_M2_충청',
    block: 'false',
    referralcode: '',
    registrant: '',
    registeredBy: '',
    extensionAmount: 20000,
    fee: 0,
  });

  // 다이얼로그 열릴 때 초기화
  useEffect(() => {
    if (open && currentUser) {
      setNewManager({
        phonenumber: '',
        username: '',
        password: '',
        grade: 'localmanager',
        appname: 'smlink_M2_충청',
        block: 'false',
        referralcode: '',
        registrant: currentUser.id || '', // 현재 로그인 관리자 전화번호를 기본값으로 설정
        registeredBy: '', // 수수료전번은 빈 값으로 설정
        extensionAmount: 20000, // 기본값 20000
        fee: 0, // 기본값 0
      });
    }
  }, [open, currentUser]);

  const handleSave = async () => {
    if (!newManager.phonenumber || !newManager.username || !newManager.password || !newManager.appname) {
      alert('필수 정보를 모두 입력하세요.');
      return;
    }

    // 연장금액 유효성 검사
    if (!newManager.extensionAmount || newManager.extensionAmount < 20000 || newManager.extensionAmount > 90000) {
      alert('연장금액은 20,000원부터 90,000원 사이로 입력하세요.');
      return;
    }

    try {
      const today = dayjs().format('YYYY-MM-DD');
      const managerData = {
        ...newManager,
        initdate: today,
      };

      await set(ref(database, `${DB_PATHS.MANAGER}/${newManager.phonenumber}`), managerData);
      
      alert('신규 관리자가 성공적으로 등록되었습니다.');
      
      // 목록 갱신 콜백 호출
      if (onManagerAdded) {
        onManagerAdded();
      }
      
      onClose();
    } catch (error) {
      console.error('관리자 등록 실패:', error);
      alert('관리자 등록에 실패했습니다.');
    }
  };

  const handleClose = () => {
    setNewManager({
      phonenumber: '',
      username: '',
      password: '',
      grade: 'localmanager',
      appname: 'smlink_M2_충청',
      block: 'false',
      referralcode: '',
              registrant: '',
        registeredBy: '',
        extensionAmount: 20000,
        fee: 0,
    });
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>신규 관리자 등록</DialogTitle>
      <DialogContent>
        <TextField
          label="전화번호"
          value={newManager.phonenumber}
          onChange={e => setNewManager({ ...newManager, phonenumber: e.target.value })}
          fullWidth
          margin="dense"
          required
        />
        <TextField
          label="이름"
          value={newManager.username}
          onChange={e => setNewManager({ ...newManager, username: e.target.value })}
          fullWidth
          margin="dense"
          required
        />
        <TextField
          label="비밀번호"
          type="password"
          value={newManager.password}
          onChange={e => setNewManager({ ...newManager, password: e.target.value })}
          fullWidth
          margin="dense"
          required
        />
        
        {/* 권한 고정 표시 */}
        <TextField
          label="권한"
          value="localmanager"
          InputProps={{
            readOnly: true,
          }}
          fullWidth
          margin="dense"
        />
        
        {/* 앱 이름 고정 표시 */}
        <TextField
          label="앱 이름"
          value="콜헌터"
          InputProps={{
            readOnly: true,
          }}
          fullWidth
          margin="dense"
          inputProps={{
            'data-value': 'smlink_M2_충청'
          }}
        />
        
        {/* 등록자 전번 필드 */}
        <TextField
          label="등록자 전번"
          value={newManager.registrant}
          onChange={e => setNewManager({ ...newManager, registrant: e.target.value })}
          fullWidth
          margin="dense"
          placeholder="등록자 전화번호"
        />
        
        {/* 연장금액 필드 */}
        <TextField
          label="연장금액"
          type="number"
          value={newManager.extensionAmount}
          onChange={e => {
            const value = e.target.value;
            const numValue = value ? Math.floor(Number(value)) : 20000;
            setNewManager({ ...newManager, extensionAmount: numValue });
          }}
          fullWidth
          margin="dense"
          placeholder="연장금액 (20,000원 ~ 90,000원)"
          inputProps={{
            min: 20000,
            max: 90000,
            step: 1000,
          }}
          helperText="20,000원 ~ 90,000원 사이로 입력하세요"
        />
        
        <TextField
          label="추천코드"
          value={newManager.referralcode}
          onChange={e => setNewManager({ ...newManager, referralcode: e.target.value })}
          fullWidth
          margin="dense"
        />
        
        <FormControl fullWidth margin="dense">
          <InputLabel>차단</InputLabel>
          <Select
            value={newManager.block}
            label="차단"
            onChange={e => setNewManager({ ...newManager, block: e.target.value })}
          >
            <MenuItem value="false">false</MenuItem>
            <MenuItem value="true">true</MenuItem>
          </Select>
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>취소</Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={!newManager.phonenumber || !newManager.username || !newManager.password}
        >
          저장
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ChungcheongManagerRegistration; 